"use client";

import { Fragment, useId, useRef, useState } from "react";
import { ArrowUp, ArrowUpRight, RotateCcw, Table2, X } from "lucide-react";
import { type ColumnDef, useTable } from "@tanstack/react-table";
import { gridTableFeatures, type GridTableFeatures } from "@product/app/workspace/table-features";
import { RecordTableGrid } from "@product/app/workspace/channels/[channelId]/record-table-grid";
import { MENTION_ENTITY_STYLES } from "@product/components/app/sessions/entity-mention-input";
import { ProvenanceAvatarStack } from "@product/components/app/runs/provenance-avatar-stack";
import { RunSourcesUsedView } from "@product/components/app/runs/run-sources-used-view";
import { ProductBridge } from "../product-bridge";
import type { ReviewExhibitContent, ReviewRecord, ReviewText } from "./content";
import styles from "./review-exhibit.module.css";

const SOURCE_USAGE = [{ key: "workspace", kind: "workspace" as const, label: "Scoped workspace records", callCount: 1, documentCount: 0, searchCount: 0, fetchCount: 0 }];

function ViewMention({ name }: { name: string }) {
  return <span className={`rounded-[4px] px-1 py-px font-medium ${MENTION_ENTITY_STYLES.view.inline}`}>@{name}</span>;
}

function Copy({ text }: { text: ReviewText }) {
  return <>{text.map((part, index) => <Fragment key={index}>{typeof part === "string" ? part : <strong>{part.strong}</strong>}</Fragment>)}</>;
}

/**
 * The same product table, mentions and receipt primitives as the landing
 * Session. The status dots and the send button carry `rounded-full`, which
 * keeps them round on a page that squares its corners (one-system).
 */
export function ReviewExhibit({ review, presentation = false, story = false }: { review: ReviewExhibitContent; presentation?: boolean; story?: boolean }) {
  const [selected, setSelected] = useState<number | null>(null);
  const [source, setSource] = useState(0);
  const evidenceId = useId();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const records = review.records;
  const record = records[selected ?? 0]!;
  const evidence = record.evidence[source]!;
  const selectRecord = (index: number) => { setSelected(index); setSource(0); };
  const columns: ColumnDef<GridTableFeatures, ReviewRecord>[] = [
    { id: "name", header: review.recordLabel, size: 170, cell: ({ row }) => <button className={styles.recordButton} aria-pressed={selected === row.index} onClick={() => selectRecord(row.index)}><i className={`${styles.dot} rounded-full`} data-tone={row.original.tone} /><span>{row.original.name}</span></button> },
    { id: "status", header: "Position", size: 145, cell: ({ row }) => <span className={styles.cell}>{row.original.status}</span> },
    { id: "date", header: review.dateLabel, size: 100, cell: ({ row }) => <span className={styles.cell}>{row.original.date}</span> },
    { id: "owner", header: "Owner", size: 112, cell: ({ row }) => <span className={styles.cell}>{row.original.owner}</span> },
  ];
  const table = useTable({ features: gridTableFeatures, data: records, columns, enableColumnResizing: false });

  return <figure className={styles.review} data-presentation={presentation} data-story={story} data-record-selected={selected !== null}>
    <ProductBridge className={styles.bridge}>
      <div className={styles.frame} data-frame-corners="">
        <header className={styles.header}><strong>{review.title}</strong><span>· {review.area}</span><div><ProvenanceAvatarStack actor={{ label: "Maya Chen", initials: "MC", imageUrl: null }} interfaceType="web" usedTwin action="started" /></div></header>
        <div className={styles.workspace}>
          <div className={styles.conversation}>
            <div className={styles.answerArea}>
              <p className={styles.question}>Across <ViewMention name={review.view} />, {review.question.charAt(0).toLowerCase() + review.question.slice(1)}</p>
              <div className={styles.answer} aria-live="polite" aria-atomic="true">
                {selected === null ? <>
                  {review.summary.map((paragraph, index) => <p key={index}><Copy text={paragraph} /></p>)}
                  <p className={styles.nextStep}>{review.nextStep}</p>
                </> : <><p><strong>{record.name}</strong></p><p>{record.answer}</p><button className={styles.back} onClick={() => { setSelected(null); setSource(0); }}><RotateCcw size={12} />Return to the full review</button></>}
              </div>
              <RunSourcesUsedView sources={SOURCE_USAGE} className={styles.receipt} />
              <div className={styles.evidenceTabs} aria-label="Inspect source evidence">{record.evidence.map((item, index) => <button key={item.source} aria-pressed={source === index} aria-controls={evidenceId} onClick={() => setSource(index)}>{item.source}<ArrowUpRight size={12} /></button>)}</div>
              <div id={evidenceId} className={styles.evidence} aria-live="polite" aria-atomic="true"><span>{record.name} / {evidence.source}</span><strong>{evidence.title}</strong><p>{evidence.detail}</p></div>
            </div>
            <div className={styles.composer} aria-hidden="true"><div><span>Run trace · Source records linked</span><span>Help improve this answer</span></div><p><span>Ask about <ViewMention name={review.view} /></span><small>AUTO</small><i className="rounded-full"><ArrowUp size={16} /></i></p></div>
          </div>
          <div className={styles.resources}>
            <div className={styles.resourcebar}><Table2 size={16} /><strong>{review.view}</strong><div aria-hidden="true"><span>Table</span><span>Board</span><span>Dashboard</span><X size={16} /></div></div>
            <div className={styles.grid}>
              <RecordTableGrid activeCell={null} editingCell={null} canEditCell={() => false} editOnClick={() => false} onSelectCell={(_, item) => selectRecord(records.indexOf(item))} onEditCell={() => {}} table={table} scrollRef={scrollRef} empty={false} />
            </div>
            <p className={styles.resourcefooter}>{records.length} records · Scope pinned to this Session</p>
          </div>
        </div>
      </div>
    </ProductBridge>
    <figcaption><span>Select a row to see the records used for the answer.</span>{review.boundary}</figcaption>
  </figure>;
}
